<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomLoadingSpinner,
  SatItemBuilder,
  useToast,
  type SatItemBuilderEmits,
} from "@chatfood/design-system";
import { computed, ref } from "vue";
import type { ItemBuilderEmits } from "./emits";
import type { ItemBuilderProps } from "./props";
import {
  useAnalytics,
  useBugReporter,
  type IDomainBasketItemModifier,
  type IDomainModifierList,
} from "#imports";
import { useAddItemBasketErrorValidation } from "./use-add-item-basket-error-validation";
import { useLocale } from "~/composables/use-locale";
import { useProductTagLabel } from "@/composables/use-product-tag-label";
import type { IDomainItem } from "@/domain/item";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useItemStore } from "~/stores/item";
import { friendlyTimeDuration } from "~/utils/time-duration";

const props = defineProps<ItemBuilderProps>();
const emits = defineEmits<ItemBuilderEmits>();

const css = bemBuilder("item-builder");

const { toastError } = useToast();
const { report } = useBugReporter();
const { trackEvent } = useAnalytics();
const { t } = useLocale();
const { productTagLabels } = useProductTagLabel();

const business = useBusinessStore();
const basket = useBasketStore();
const itemStore = useItemStore();
const featureMatrix = useFeatureMatrixStore();

const isAdding = ref(false);

const isLoading = computed(() => itemStore.isProcessing);

const isEditMode = computed(() => !!props.editLineId);
const editBasketItem = computed(() =>
  basket.items.find((i) => i.lineId === props.editLineId),
);

function applyBasketSelections(
  modifierLists: IDomainModifierList[],
  basketModifiers: IDomainBasketItemModifier[] | undefined,
): IDomainModifierList[] {
  if (!basketModifiers?.length) return modifierLists;

  // Sum quantities across duplicate IDs — combo items store
  // the same modifier ID once per position, each with quantity: 1.
  const basketMap = new Map<string, IDomainBasketItemModifier>();
  const remaining = new Map<string, number>();
  for (const m of basketModifiers) {
    if (!basketMap.has(m.id)) basketMap.set(m.id, m);
    remaining.set(m.id, (remaining.get(m.id) ?? 0) + m.quantity);
  }

  const consumeMatch = (id: string) => {
    remaining.set(id, (remaining.get(id) ?? 0) - 1);
  };

  const hasRemaining = (id: string) => (remaining.get(id) ?? 0) > 0;

  return modifierLists.map((list) => {
    const isRadio = list.selectType === "radio";
    let radioSelected = false;

    return {
      ...list,
      modifiers: list.modifiers.map((modifier) => {
        const entry = basketMap.get(modifier.id);
        const blocked =
          !entry ||
          !hasRemaining(modifier.id) ||
          (isRadio && radioSelected) ||
          modifier.inStock === false;

        if (blocked) return { ...modifier, defaultQuantity: 0 };

        if (isRadio) radioSelected = true;
        consumeMatch(modifier.id);

        return {
          ...modifier,
          defaultQuantity: 1,
          modifierLists: applyBasketSelections(
            modifier.modifierLists,
            entry.modifiers,
          ),
        };
      }),
    };
  });
}

const item = computed(() => {
  const base = {
    ...itemStore.state,
    modifierLists: itemStore.state.modifierLists.map((list) => ({
      ...list,
      modifiers: list.modifiers.map((modifier) => ({ ...modifier })),
    })),
  };

  if (!isEditMode.value || !editBasketItem.value) return base;

  return {
    ...base,
    modifierLists: applyBasketSelections(
      base.modifierLists,
      editBasketItem.value.modifiers,
    ),
  };
});

type ILabelledItem = Omit<IDomainItem, "modifierLists"> & {
  allergens: string[];
  consumables: string[];
  modifierLists: Array<
    Omit<IDomainModifierList, "modifiers"> & { modifiers: ILabelledItem[] }
  >;
};

function withTagLabels(entry: IDomainItem): ILabelledItem {
  return {
    ...entry,
    allergens: productTagLabels(entry.allergenTags),
    consumables: productTagLabels(entry.consumableTags),
    modifierLists: entry.modifierLists.map((list) => ({
      ...list,
      modifiers: list.modifiers.map(withTagLabels),
    })),
  };
}

const labelledItem = computed(() => withTagLabels(item.value));

const { handleBasketError } = useAddItemBasketErrorValidation();

async function onAddToBasket(payload: SatItemBuilderEmits["addToBasket"][0]) {
  isAdding.value = true;

  try {
    if (props.editLineId) {
      await basket.updateItemQuantity(props.editLineId, 0);
    }

    try {
      await basket.addItem({
        ...payload,
        menuId: itemStore.menuId ?? props.menuId,
        maxSelection: itemStore.maxSelection,
      });
    } catch (error) {
      if (props.editLineId) {
        toastError(t("components.item_builder.update_item_delete_error"));
        onClose();
        return;
      }
      throw error;
    }

    trackEvent({
      name: "addItemToBasket",
      payload: {
        id: payload.id,
        name: itemStore.name,
        quantity: payload.quantity,
        price: {
          value: payload.unitPrice,
          currency: basket.currency,
        },
        flow: props.flow,
      },
    });

    onClose();
  } catch (error) {
    const wasHandled = handleBasketError(
      error,
      item.value.modifierLists,
      payload.id,
    );

    if (wasHandled) return;

    report(error);
    toastError(t("components.item_builder.add_item_general_error"));
  } finally {
    isAdding.value = false;
  }
}

const onClose = () => {
  emits("close");
};

const itemQuantityLimits = computed(() => {
  const basketLimits = basket.itemLimits?.[item.value.plu];
  const itemMax = item.value.maxSelection;
  const editQuantity = isEditMode.value
    ? (editBasketItem.value?.quantity ?? 0)
    : 0;

  const remaining = basketLimits?.quantity.remaining;
  const remainingQuantity =
    remaining !== undefined ? remaining + editQuantity : (itemMax ?? undefined);
  const maxQuantity = basketLimits?.quantity.maximum ?? itemMax;

  return {
    maxQuantity: remainingQuantity,
    maxQuantityLabel: maxQuantity
      ? t("components.item_builder.max_quantity_is", {
          maxQuantity,
        })
      : undefined,
  };
});

const warningMessage = computed(() => {
  const minimumLeadTimeInMinutes = itemStore.minimumLeadTimeInMinutes;

  const friendlyTimeInAdvance = friendlyTimeDuration({
    minutes: minimumLeadTimeInMinutes,
    translationConfig: { t },
  });

  if (minimumLeadTimeInMinutes > 0) {
    return t("components.item_builder.minimum_lead_time_warning", {
      timeIndAdvance: friendlyTimeInAdvance,
    });
  }

  return "";
});

const editModeProps = computed(() => {
  if (!isEditMode.value || !editBasketItem.value) return {};

  return {
    initialNotes: editBasketItem.value.notes,
    initialQuantity: editBasketItem.value.quantity,
    ctaLabel: t("components.item_builder.update_basket_cta"),
  };
});
</script>

<template>
  <div :class="css('')">
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <SatItemBuilder
      v-else
      :item="labelledItem"
      :max-item-quantity="itemQuantityLimits.maxQuantity"
      :max-quantity-label="itemQuantityLimits.maxQuantityLabel"
      :discount-applied="basket.discount"
      :item-display-view="business.menuDisplayLayout === 'grid' ? 'col' : 'row'"
      :is-cta-loading="isAdding"
      :display-notes="featureMatrix.isToggleEnabled('collect-item-notes')"
      :warning-message="warningMessage"
      :is-cta-disabled="!!warningMessage"
      :required-label="t('components.item_builder.required')"
      :out-of-stock-label="t('components.item_builder.out_of_stock')"
      v-bind="editModeProps"
      @add-to-basket="onAddToBasket"
      @close="onClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.item-builder {
  position: relative;
  max-width: 100%;
  height: 100%;
  min-height: 400px;

  &__loading {
    max-width: 100%;
    width: 100%;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (width > $breakpoint-sm) {
      width: 580px;
      height: 580px;
    }
  }

  :deep(.sat-navigation__right-cta) {
    @media (width > $breakpoint-sm) {
      display: none;
    }
  }
}
</style>
